export type LoadErrorRecoveryKind = "stale-assets" | "transient-load";

const STALE_ASSET_ERROR_PATTERNS = [
  "chunkloaderror",
  "css_chunk_load_failed",
  "loading chunk",
  "loading css chunk",
  // Turbopack's runtime-base reports a failed dynamic import as "Failed to
  // load chunk <path> from module <id>" — note "load chunk", not the webpack
  // "Loading chunk N failed" above, so it needs its own entry to reach the
  // stale-asset reload path.
  "failed to load chunk",
  "importing a module script failed",
  "error loading dynamically imported module",
  "failed to fetch dynamically imported module",
  "module factory is not available",
  "stale browser cache",
  "service worker serving outdated responses",
  "rendered more hooks than during the previous render",
  "rendered fewer hooks than expected",
  "change in the order of hooks",
  "minified react error #310",
  "minified react error #311",
] as const;

const TRANSIENT_LOAD_ERROR_PATTERNS = [
  /^load failed\.?$/i,
  /^failed to fetch\.?$/i,
  /^fetch failed\.?$/i,
  /^network error\.?$/i,
  /^networkerror when attempting to fetch resource\.?$/i,
  /^the network connection was lost\.?$/i,
] as const;

function loadErrorText(error: Error & { digest?: string }) {
  return `${error.name}\n${error.message}\n${error.stack ?? ""}`;
}

/**
 * Classify failures where a hard reload can restore a coherent deployment.
 *
 * The exact Safari `TypeError: Load failed` and Turbopack "module factory is
 * not available" messages are production fixtures captured by PostHog. Keep
 * this intentionally narrower than a general network-error detector: normal
 * application/domain errors must stay on the regular retry path.
 */
export function getLoadErrorRecoveryKind(
  error: Error & { digest?: string },
): LoadErrorRecoveryKind | null {
  const text = loadErrorText(error).toLowerCase();
  if (STALE_ASSET_ERROR_PATTERNS.some((pattern) => text.includes(pattern))) {
    return "stale-assets";
  }

  const message = error.message.trim();
  if (TRANSIENT_LOAD_ERROR_PATTERNS.some((pattern) => pattern.test(message))) {
    return "transient-load";
  }

  return null;
}

export function isRecoverableLoadError(error: Error & { digest?: string }) {
  return getLoadErrorRecoveryKind(error) !== null;
}

export function getLoadErrorSignature(error: Error & { digest?: string }) {
  return [error.digest, error.name, error.message?.slice(0, 160)]
    .filter(Boolean)
    .join(":");
}
